import { createContext, useContext } from "react";
import type { Lang } from "./types";

/**
 * Every sentence the portal says, in Swedish (the default) and English.
 *
 * ## Copy names a real mechanism
 *
 * Each claim below is something the API does today
 * (`product-copy-must-name-a-real-mechanism`). In particular:
 *
 * - Sign-up, resend and forgot-password say "if the address can be used, a
 *   mail is on its way" - never "we sent you a mail" - because the server
 *   answers identically whether or not the address has an account, and sends
 *   a different mail (or none) when it does.
 * - No prices, offers or payment lines: the per-title shop is retired
 *   (organisation plans, `docs/design/organisation-plans.md`), and plans
 *   are priced ex VAT, so "prices incl. VAT" would be false.
 * - "Mest populära" is the portal's own paid sales of the last 90 days
 *   (`portalCatalogueIndex`).
 * - The score on a title page names where it came from (IMDb, TMDB, the
 *   provider, or "entered manually"): it is the `title_ratings.source` of
 *   the one row shown, nothing aggregated.
 * - The footer's TMDB line ("Viss information och betyg kommer från TMDB"
 *   and TMDB's required notice) shows only when the context says TMDB data
 *   is stored (`attributions`), i.e. after a TMDB backfill wrote something.
 * - Min lista is saved on the server for this account (`consumer_watchlist`),
 *   so "saved" is true on every device that signs in to this portal.
 * - Erasure keeps the receipts (bookkeeping), without the email or name - the
 *   order rows' address is overwritten by the same transaction.
 *
 * `{name}` placeholders are filled by {@link format}.
 */
const sv = {
  "lang.sv": "Svenska",
  "lang.en": "English",
  "lang.label": "Språk",

  "nav.browse": "Filmer",
  "nav.home": "Hem",
  "nav.search": "Sök",
  "nav.my": "Mitt innehåll",
  "nav.account": "Konto",
  "nav.login": "Logga in",
  "nav.plan": "Abonnemang",
  "common.cancel": "Avbryt",
  "watch.purchaser":
    "Inköparkontot sköter abonnemanget och kan inte spela filmer. Dela ett tittarkonto för att titta.",
  "pick.add": "Lägg till",
  "pick.left": "Ni kan välja {left} filmer till.",
  "pick.included": "Ingår i ert abonnemang",
  "pick.added": "Tillagd - ingår nu i ert abonnemang",
  "pick.noneLeft": "Ni har inga filmval kvar. Uppgradera för att välja fler.",
  "pick.confirmTitle": "Lägga till \u201d{title}\u201d i ert abonnemang?",
  "pick.confirmBody":
    "Är du helt säker? Valet är slutgiltigt - filmen går inte att byta ut. Efter det har ni {after} filmval kvar.",
  "pick.confirmYes": "Ja, jag är säker - lägg till",
  "plan.title": "Ert abonnemang",
  "account.manageSubscription": "Välj filmer och dela tittarkonton",
  "plan.film1": "1 film",
  "plan.filmsN": "{n} filmer",
  "plan.price": "{price} per år exkl. moms · förnyas {renews}",
  "plan.filmsTitle": "Era filmer ({picked} av {of})",
  "plan.filmsTitleAll": "Era filmer",
  "plan.fullLibraryHint":
    "Ert abonnemang omfattar hela biblioteket - inget behöver väljas.",
  "plan.noPicks": "Ni har inte valt några filmer än.",
  "plan.pickTitle": "Välj filmer ({n} kvar)",
  "plan.pickHint":
    "Markera de filmer ni vill ha. Valda filmer ingår i abonnemanget så länge det gäller.",
  "plan.filter": "Sök bland filmerna",
  "plan.pickSave": "Lägg till valda filmer ({n})",
  "plan.pickPermanent": "Ett val kan inte bytas ut i efterhand.",
  "plan.picked": "De valda filmerna ingår nu i ert abonnemang.",
  "plan.upgradeTitle": "Uppgradera",
  "plan.upgradeHint":
    "Ni betalar mellanskillnaden för resten av abonnemangsåret. Förnyelsedatumet ändras inte, och det går inte att gå tillbaka till ett mindre abonnemang.",
  "plan.noUpgrades": "Ni har redan det största abonnemanget.",
  "plan.upgradePrice": "{yearly} per år · att betala nu {now} exkl. moms",
  "plan.upgrade": "Uppgradera",
  "plan.upgradeTo": "Uppgradera till {plan}",
  "plan.currentBand": "Det ni har i dag",
  "plan.upgradeConfirm": "Bekräfta - betala {now}",
  "plan.upgraded": "Ert abonnemang är nu {plan}.",
  "plan.err.tooMany": "Ni har inte så många filmval kvar.",
  "plan.err.notInCatalogue": "En av filmerna finns inte längre i katalogen.",
  "plan.err.fullLibrary": "Ert abonnemang omfattar redan hela biblioteket.",
  "plan.err.notUpgrade":
    "Det går bara att uppgradera till ett större abonnemang.",
  "plan.err.notSold": "Det abonnemanget säljs inte längre.",
  "plan.err.notPurchaser": "Bara organisationens inköpare kan se abonnemanget.",
  "plan.err.noPlan": "Organisationen har inget aktivt abonnemang.",
  "viewers.title": "Tittarkonton",
  "viewers.hint":
    "Dela era filmer med kollegor. De får ett mejl med en länk för att välja lösenord, och ser bara era filmer. Ni kan ta bort en person när som helst.",
  "viewers.email": "E-postadress till kollegan",
  "viewers.share": "Dela",
  "viewers.none": "Ni har inte delat med någon än.",
  "viewers.statusActive": "Har tillgång",
  "viewers.statusInvited": "Inbjuden",
  "viewers.revoke": "Ta bort",
  "viewers.revokeFor": "Ta bort {email}",
  "viewers.revokeConfirm": "Ja, ta bort tillgången",
  "viewers.revokedList": "Borttagna ({n})",
  "viewers.shared": "{email} har fått en inbjudan.",
  "viewers.restored": "{email} har fått tillgång igen.",
  "viewers.revoked": "{email} har inte längre tillgång och har loggats ut.",
  "viewers.err.emailTaken":
    "Den adressen används redan av ett annat konto här.",
  "viewers.err.alreadyShared": "Ni har redan delat med den adressen.",
  "viewers.err.invalid": "Skriv en giltig e-postadress.",
  "viewer.emptyTitle": "Inget att titta på än",
  "viewer.empty": "När er organisation har valt filmer visas de här.",
  "viewer.play": "Spela {title}",
  "nav.register": "Ansök om konto",
  "nav.main": "Huvudmeny",
  "nav.skip": "Hoppa till innehållet",
  "nav.backbar": "Tillbaka",

  "common.loading": "Laddar…",
  "common.retry": "Försök igen",
  "common.error": "Något gick fel. Försök igen om en stund.",
  "common.offline":
    "Kunde inte nå tjänsten. Kontrollera din anslutning och försök igen.",
  "common.back": "Tillbaka",
  "watch.starting": "Startar…",
  "watch.expired": "Hyrtiden för den här titeln är slut.",
  "watch.revoked": "Den här hyran har återkallats.",
  "watch.missing": "Titeln finns inte här.",
  "watch.busy": "För många försök just nu. Vänta en stund och försök igen.",
  "watch.unavailable":
    "Titeln kan inte spelas just nu. Försök igen om en stund.",
  "watch.signedOut": "Du är utloggad. Logga in igen för att fortsätta titta.",
  "watch.takenOver":
    "Ditt konto spelar nu på en annan enhet. Ett konto kan titta på en sak i taget.",
  "watch.ended": "Uppspelningen avslutades. Starta igen för att fortsätta.",
  "watch.browserUnsupported":
    "Den här titeln är skyddad och kan inte spelas i den här webbläsaren än. Prova Chrome, Edge eller Firefox.",
  "watch.finished": "Slut.",
  "watch.fail.licence": "Titeln får inte spelas i den här webbläsaren.",
  "watch.fail.licenceExpired":
    "Titeln slutade vara tillgänglig. Försök starta den igen.",
  "watch.fail.network":
    "Anslutningen bröts. Kontrollera nätet och försök igen.",
  "watch.fail.codec": "Webbläsaren kan inte spela titelns videoformat.",
  "watch.fail.unknown": "Titeln kunde inte spelas.",
  "watch.code": "Felkod",
  "watch.seek": "Tidslinje",
  "watch.play": "Spela (mellanslag)",
  "watch.pause": "Pausa (mellanslag)",
  "watch.back10": "Bakåt 10 sekunder (vänsterpil)",
  "watch.fwd10": "Framåt 10 sekunder (högerpil)",
  "watch.mute": "Ljud av (M)",
  "watch.unmute": "Ljud på (M)",
  "watch.volume": "Volym",
  "watch.tracks": "Undertexter och ljud",
  "watch.subtitlesOff": "Av",
  "watch.pip": "Bild i bild",
  "watch.fullscreen": "Helskärm (F)",
  "watch.locked": "Du har inte hyrt den här titeln.",
  "common.email": "E-postadress",
  "common.password": "Lösenord",
  "common.passwordHint": "Minst 10 tecken.",
  "common.save": "Spara",
  "common.saving": "Sparar…",
  "common.saved": "Sparat.",
  "common.showMore": "Visa fler",
  "common.close": "Stäng",

  "unavailable.title": "Sidan är inte tillgänglig",
  "unavailable.body":
    "Det finns ingen aktiv tjänst på den här adressen just nu.",
  "unavailable.titleEn": "This page is not available",
  "unavailable.bodyEn": "There is no active service at this address right now.",

  "footer.terms": "Villkor",
  "footer.privacy": "Integritet",
  "footer.support": "Support: {email}",

  "browse.titles": "Titlar",
  "browse.empty": "Det finns inget att hyra här just nu.",
  "browse.truncated":
    "Katalogen är stor och visas inte i sin helhet här. Bläddra per genre.",
  "browse.unavailable":
    "En del av katalogen kan inte visas just nu. Försök igen om en stund.",
  "store.search.label": "Sök film",
  "store.search.placeholder": "Sök på titel",
  "store.search.clear": "Rensa sökningen",
  "store.search.hits": "{count} träffar",
  "store.search.hit": "1 träff",
  "store.search.empty": "Inga träffar för ”{q}”.",
  "store.search.short": "Skriv minst två tecken.",
  "store.genres": "Genrer",
  "store.rail.prev": "Föregående i {rail}",
  "store.rail.next": "Nästa i {rail}",
  "store.rail.seeAll": "Visa alla",
  "store.rail.seeAllIn": "Visa alla: {rail}",
  "store.rail.error": "Kunde inte ladda raden.",

  "info.title": "Information",
  "info.kind.video": "Video",
  "info.kind.videoLength": "Video · {length}",
  "info.kind.pdf": "PDF",
  "info.kind.pdfPage": "PDF · 1 sida",
  "info.kind.pdfPages": "PDF · {count} sidor",
  "info.emptyTitle": "Ingen information än",
  "info.empty":
    "Här visas filmklipp och dokument från {portal} när de läggs upp.",
  "info.gone": "Det här finns inte längre",
  "info.backToList": "Till all information",
  "info.download": "Ladda ner",
  "info.page": "Sida {page} av {count}",
  "info.clipFailed":
    "Klippet kunde inte spelas. Ladda om sidan och försök igen.",
  "info.pdfFailed":
    "Dokumentet kunde inte visas. Du kan ladda ner det i stället.",
  "store.allTitles": "Alla filmer",
  "genre.sortBy": "Sortera efter",
  "genre.sort.popular": "Mest populära",
  "genre.sort.az": "A–Ö",
  "genre.sort.year": "Utgivningsår",
  "genre.sort.length": "Längd",
  "genre.notFound": "Genren finns inte här.",
  "genre.count": "{count} titlar",
  "card.minutes": "{n} min",
  "card.hoursMinutes": "{h} h {m} min",

  "title.locked": "Inte hyrd",
  "title.rented": "Hyrd",
  "title.notFound": "Titeln finns inte här.",
  "title.addToList": "Min lista",
  "title.addToListLabel": "Lägg till på Min lista",
  "title.onList": "På Min lista",
  "title.removeFromListLabel": "Ta bort från Min lista",
  "title.listFailed": "Det gick inte att ändra Min lista. Försök igen.",
  "title.rentedEnds": "Hyrd · utgår om {time}",
  "title.rentedStartWithin": "Hyrd · starta inom {time}",
  "title.rentedNoLimit": "Hyrd · ingen tidsgräns",
  "title.rentedExpired": "Hyrtiden har gått ut",
  "title.originalTitle": "Originaltitel: {title}",
  "title.director": "Regi",
  "title.cast": "Medverkande",
  "title.audio": "Ljud",
  "title.play": "Spela",
  "title.playEpisode": "Spela S{s}:A{e}",
  "title.episodeList": "Avsnitt",
  "title.seasonTabs": "Säsong",
  "title.seasonN": "Säsong {n}",
  "title.episodeLocked": "Ingår inte i din hyra",
  "title.episodeExpired": "Hyrtiden är slut",
  "title.subtitles": "Undertexter",
  "title.aboutFilm": "Om filmen",
  "title.aboutSeries": "Om serien",
  "title.details": "Uppgifter",
  "title.seasons": "{n} säsonger",
  "title.season": "1 säsong",
  "title.episodes": "{n} avsnitt",
  "title.age": "Åldersgräns {age} år",
  "title.ageAll": "Barntillåten",
  "title.score.imdb": "Betyg på IMDb: {score} av 10",
  "title.score.tmdb": "Betyg på TMDB: {score} av 10",
  "title.score.provider": "Betyg från leverantören: {score} av 10",
  "title.score.manual": "Betyg inlagt manuellt: {score} av 10",
  "title.score.votes": "{n} röster",

  "return.browse": "Tillbaka till filmerna",

  "my.title": "Mitt innehåll",
  "my.loginFirst": "Logga in för att se ditt innehåll.",
  "my.empty": "Du har inte hyrt något ännu.",
  "my.active": "Kan ses nu",
  "my.notStarted": "Inte startad",
  "my.expired": "Utgångna",
  "my.left": "{time} kvar",
  "my.noLimit": "Ingen tidsgräns",
  "my.startBy": "Starta senast {date}",
  "my.clockOnPlay": "Hyrestiden börjar när du startar titeln i appen.",
  "my.endedAt": "Gick ut {date}",
  "time.lessThanMinute": "mindre än 1 min",

  "auth.loginTitle": "Logga in",
  "auth.login": "Logga in",
  "auth.loggingIn": "Loggar in…",
  "auth.invalid": "Fel e-postadress eller lösenord.",
  "auth.notVerified":
    "Bekräfta din e-postadress först – titta efter mejlet från oss.",
  "auth.disabled": "Det här kontot är avstängt.",
  "auth.rateLimited": "För många försök. Vänta en stund och försök igen.",
  "auth.forgot": "Glömt lösenordet?",
  "auth.noAccount": "Inget konto?",
  "auth.resend": "Skicka bekräftelselänken igen",
  "auth.name": "Namn (frivilligt)",
  "apply.title": "Ansök om konto",
  "apply.intro":
    "Konton hos {provider} är för organisationer, till exempel skolor och bibliotek. Fyll i uppgifterna nedan så granskar vi ansökan. Du får ett mejl när kontot är klart att använda.",
  "apply.companyName": "Företagsnamn",
  "apply.orgNumber": "Organisationsnummer",
  "apply.orgNumberHint": "Till exempel 556074-3089",
  "apply.attention": "Att",
  "apply.street": "Adress",
  "apply.postcode": "Postnummer",
  "apply.city": "Ort",
  "apply.contactEmail": "E-post till kontaktperson",
  "apply.invalid.companyName": "Skriv organisationens namn.",
  "apply.invalid.orgNumber":
    "Kontrollera organisationsnumret - det ska vara 10 siffror, till exempel 556074-3089.",
  "apply.invalid.attention": "Skriv vem vi ska kontakta.",
  "apply.invalid.street": "Skriv gatuadressen.",
  "apply.invalid.postcode": "Postnumret ska vara fem siffror.",
  "apply.invalid.city": "Skriv orten.",
  "apply.invalid.contactEmail": "Skriv en giltig e-postadress.",
  "apply.band": "Hur många tittar vid en visning?",
  "apply.bandHint":
    "Antal personer som ser filmen samtidigt, till exempel en klass.",
  "apply.level": "Hur många filmer?",
  "apply.film1": "välj 1 film",
  "apply.films": "välj {n} filmer",
  "apply.fullLibrary": "alla filmer",
  "apply.perYear": "{price} per år exkl. moms ({inclVat} inkl. moms)",
  "apply.invalid.plan": "Välj hur många som tittar och hur många filmer.",
  "apply.closed": "{provider} tar inte emot ansökningar just nu.",
  "apply.send": "Skicka ansökan",
  "apply.sending": "Skickar…",
  "apply.doneTitle": "Tack för din ansökan",
  "apply.done":
    "{provider} granskar ansökan och hör av sig till {email}. När den är godkänd får du ett mejl med en länk för att välja lösenord.",
  "apply.backToStore": "Tillbaka till filmerna",
  "auth.haveAccount": "Har du redan ett konto?",
  "auth.tooShort": "Lösenordet måste vara minst 10 tecken.",
  "auth.tooLong": "Lösenordet är för långt.",
  "auth.breached":
    "Det här lösenordet har förekommit i en dataläcka. Välj ett annat.",
  "auth.badRequest": "Kontrollera uppgifterna och försök igen.",
  "auth.confirmTitle": "Bekräfta e-postadress",
  "auth.confirming": "Bekräftar…",
  "auth.confirmed": "E-postadressen är bekräftad. Nu kan du logga in.",
  "auth.badLink": "Länken är ogiltig eller har gått ut.",
  "auth.noToken": "Länken saknar sin kod. Öppna länken direkt från mejlet.",
  "auth.resendTitle": "Skicka bekräftelselänken igen",
  "auth.resendSend": "Skicka",
  "auth.resendSent":
    "Om det finns ett obekräftat konto för {email} har vi skickat en ny länk dit.",
  "auth.forgotTitle": "Glömt lösenordet",
  "auth.forgotSend": "Skicka återställningslänk",
  "auth.forgotSent":
    "Om det finns ett konto för {email} har vi skickat en länk dit. Den gäller i en timme.",
  "auth.resetTitle": "Välj nytt lösenord",
  "auth.newPassword": "Nytt lösenord",
  "auth.resetSave": "Spara lösenordet",
  "auth.resetDone":
    "Lösenordet är bytt och alla enheter har loggats ut. Logga in med det nya lösenordet.",
  "auth.inviteTitle": "Välkommen",
  "auth.inviteBody":
    "Du har bjudits in via din skola. Välj ett lösenord för att aktivera kontot.",
  "auth.inviteSave": "Aktivera kontot",
  "auth.inviteDone": "Kontot för {email} är aktiverat. Nu kan du logga in.",
  "auth.inviteDoneSwitched":
    "Kontot för {email} är aktiverat. Du var inloggad som {previous} och har loggats ut - logga in som {email}.",
  "auth.signedInAs": "Du är inloggad som {email}.",
  "auth.continueAs": "Fortsätt",
  "auth.switchAccount": "Logga ut och byt konto",
  "auth.inviteGone": "Inbjudan är ogiltig, redan använd eller har gått ut.",
  "auth.signedOut": "Du är utloggad.",

  "account.title": "Konto",
  "account.loginFirst": "Logga in för att se ditt konto.",
  "account.profile": "Profil",
  "account.name": "Namn",
  "account.locale": "Språk i mejl",
  "account.marketing": "Nyheter och erbjudanden via e-post",
  "account.currentPassword": "Nuvarande lösenord",
  "account.wrongPassword": "Fel lösenord.",
  "account.passwordTitle": "Byt lösenord",
  "account.passwordChange": "Byt lösenord",
  "account.passwordChanged":
    "Lösenordet är bytt. Dina andra enheter har loggats ut.",
  "account.devices": "Inloggade enheter",
  "account.device.web": "Webbläsare",
  "account.device.app": "App",
  "account.device.current": "Den här enheten",
  "account.device.lastUsed": "Senast använd {date}",
  "account.device.signOut": "Logga ut",
  "account.device.none": "Inga inloggade enheter.",
  "account.logout": "Logga ut",
  "account.logoutAll": "Logga ut från alla enheter",
  "account.logoutAllBody":
    "Loggar ut den här webbläsaren och alla appar där du är inloggad.",
  "account.unverified": "Din e-postadress är inte bekräftad ännu.",

  /* Store design (portal appearance) */
  "card.expiresIn": "Utgår om {time}",
  "card.notStarted": "Hyrd · ej startad",
  "card.noLimit": "Hyrd · ingen tidsgräns",
  "card.expired": "Hyrtiden har gått ut",
  "store.genres.prev": "Föregående genrer",
  "store.genres.next": "Fler genrer",
  "store.empty.title": "Butiken fylls på",
  "store.empty.body":
    "Det finns inget att hyra här just nu. Titta in igen lite senare.",
  "store.search.emptyHint": "Prova ett annat ord, eller bläddra i en genre.",
  "my.status.active": "Kan ses nu",
  "my.status.notStarted": "Ej startad",
  "my.status.expired": "Utgången",
  "my.emptyTitle": "Här hamnar det du hyr",
  "my.discover": "Upptäck filmerna",
  "my.list": "Min lista",
  "my.listEmpty":
    "Inget sparat ännu. Tryck på Min lista på en films sida för att spara den här.",
  "footer.nav": "Om tjänsten",
  "footer.tmdb": "Viss information och betyg kommer från",
  // TMDB's required notice, in the words their terms give (English).
  "footer.tmdbNotice":
    "This product uses the TMDB API but is not endorsed or certified by TMDB.",
};

export type MessageKey = keyof typeof sv;

const en: Record<MessageKey, string> = {
  "lang.sv": "Svenska",
  "lang.en": "English",
  "lang.label": "Language",

  "nav.browse": "Films",
  "nav.home": "Home",
  "nav.search": "Search",
  "nav.my": "My content",
  "nav.account": "Account",
  "nav.login": "Log in",
  "nav.plan": "Subscription",
  "common.cancel": "Cancel",
  "watch.purchaser":
    "The purchaser account manages the subscription and cannot play films. Share a viewer account to watch.",
  "pick.add": "Add",
  "pick.left": "You can choose {left} more films.",
  "pick.included": "Included in your subscription",
  "pick.added": "Added - now included in your subscription",
  "pick.noneLeft": "You have no film choices left. Upgrade to choose more.",
  "pick.confirmTitle": "Add \u201c{title}\u201d to your subscription?",
  "pick.confirmBody":
    "Are you completely sure? The choice is final - the film cannot be swapped. After this you have {after} film choices left.",
  "pick.confirmYes": "Yes, I am sure - add it",
  "plan.title": "Your subscription",
  "account.manageSubscription": "Choose films and share viewer accounts",
  "plan.film1": "1 film",
  "plan.filmsN": "{n} films",
  "plan.price": "{price} per year ex VAT · renews {renews}",
  "plan.filmsTitle": "Your films ({picked} of {of})",
  "plan.filmsTitleAll": "Your films",
  "plan.fullLibraryHint":
    "Your subscription covers the whole library - nothing to choose.",
  "plan.noPicks": "You have not chosen any films yet.",
  "plan.pickTitle": "Choose films ({n} left)",
  "plan.pickHint":
    "Tick the films you want. Chosen films stay in your subscription for as long as it runs.",
  "plan.filter": "Search the films",
  "plan.pickSave": "Add the chosen films ({n})",
  "plan.pickPermanent": "A choice cannot be swapped afterwards.",
  "plan.picked": "The chosen films are now part of your subscription.",
  "plan.upgradeTitle": "Upgrade",
  "plan.upgradeHint":
    "You pay the difference for the rest of the subscription year. The renewal date does not change, and you cannot go back to a smaller subscription.",
  "plan.noUpgrades": "You already have the largest subscription.",
  "plan.upgradePrice": "{yearly} per year · pay {now} now ex VAT",
  "plan.upgrade": "Upgrade",
  "plan.upgradeTo": "Upgrade to {plan}",
  "plan.currentBand": "What you have today",
  "plan.upgradeConfirm": "Confirm - pay {now}",
  "plan.upgraded": "Your subscription is now {plan}.",
  "plan.err.tooMany": "You do not have that many film choices left.",
  "plan.err.notInCatalogue": "One of the films is no longer in the catalogue.",
  "plan.err.fullLibrary": "Your subscription already covers the whole library.",
  "plan.err.notUpgrade": "You can only upgrade to a larger subscription.",
  "plan.err.notSold": "That plan is no longer sold.",
  "plan.err.notPurchaser":
    "Only the organisation's purchaser can see the subscription.",
  "plan.err.noPlan": "The organisation has no active subscription.",
  "viewers.title": "Viewer accounts",
  "viewers.hint":
    "Share your films with colleagues. They get an email with a link to choose a password, and only see your films. You can remove someone at any time.",
  "viewers.email": "Your colleague's email address",
  "viewers.share": "Share",
  "viewers.none": "You have not shared with anyone yet.",
  "viewers.statusActive": "Has access",
  "viewers.statusInvited": "Invited",
  "viewers.revoke": "Remove",
  "viewers.revokeFor": "Remove {email}",
  "viewers.revokeConfirm": "Yes, remove access",
  "viewers.revokedList": "Removed ({n})",
  "viewers.shared": "{email} has been invited.",
  "viewers.restored": "{email} has access again.",
  "viewers.revoked": "{email} no longer has access and has been signed out.",
  "viewers.err.emailTaken":
    "That address is already used by another account here.",
  "viewers.err.alreadyShared": "You have already shared with that address.",
  "viewers.err.invalid": "Enter a valid email address.",
  "viewer.emptyTitle": "Nothing to watch yet",
  "viewer.empty": "When your organisation has chosen films, they appear here.",
  "viewer.play": "Play {title}",
  "nav.register": "Apply for an account",
  "nav.main": "Main menu",
  "nav.skip": "Skip to content",
  "nav.backbar": "Back",

  "common.loading": "Loading…",
  "common.retry": "Try again",
  "common.error": "Something went wrong. Please try again in a moment.",
  "common.offline":
    "Could not reach the service. Check your connection and try again.",
  "common.back": "Back",
  "watch.starting": "Starting…",
  "watch.expired": "The rental for this title has ended.",
  "watch.revoked": "This rental has been withdrawn.",
  "watch.missing": "This title is not here.",
  "watch.busy": "Too many attempts just now. Wait a moment and try again.",
  "watch.unavailable":
    "This title cannot be played right now. Try again shortly.",
  "watch.signedOut": "You are signed out. Sign in again to keep watching.",
  "watch.takenOver":
    "Your account is now playing on another device. One account watches one thing at a time.",
  "watch.ended": "Playback ended. Start again to continue.",
  "watch.browserUnsupported":
    "This title is protected and cannot play in this browser yet. Try Chrome, Edge or Firefox.",
  "watch.finished": "The end.",
  "watch.fail.licence": "This title may not be played in this browser.",
  "watch.fail.licenceExpired":
    "This title stopped being available. Try starting it again.",
  "watch.fail.network":
    "The connection dropped. Check the network and try again.",
  "watch.fail.codec": "This browser cannot play this title's video format.",
  "watch.fail.unknown": "This title could not be played.",
  "watch.code": "Error code",
  "watch.seek": "Timeline",
  "watch.play": "Play (space)",
  "watch.pause": "Pause (space)",
  "watch.back10": "Back 10 seconds (left arrow)",
  "watch.fwd10": "Forward 10 seconds (right arrow)",
  "watch.mute": "Mute (M)",
  "watch.unmute": "Unmute (M)",
  "watch.volume": "Volume",
  "watch.tracks": "Subtitles and audio",
  "watch.subtitlesOff": "Off",
  "watch.pip": "Picture in picture",
  "watch.fullscreen": "Full screen (F)",
  "watch.locked": "You have not rented this title.",
  "common.email": "Email address",
  "common.password": "Password",
  "common.passwordHint": "At least 10 characters.",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saved": "Saved.",
  "common.showMore": "Show more",
  "common.close": "Close",

  "unavailable.title": "Sidan är inte tillgänglig",
  "unavailable.body":
    "Det finns ingen aktiv tjänst på den här adressen just nu.",
  "unavailable.titleEn": "This page is not available",
  "unavailable.bodyEn": "There is no active service at this address right now.",

  "footer.terms": "Terms",
  "footer.privacy": "Privacy",
  "footer.support": "Support: {email}",

  "browse.titles": "Titles",
  "browse.empty": "There is nothing to rent here right now.",
  "browse.truncated":
    "The catalogue is large and is not shown in full here. Browse by genre.",
  "browse.unavailable":
    "Part of the catalogue cannot be shown right now. Please try again in a moment.",
  "store.search.label": "Search films",
  "store.search.placeholder": "Search by title",
  "store.search.clear": "Clear the search",
  "store.search.hits": "{count} results",
  "store.search.hit": "1 result",
  "store.search.empty": "No results for “{q}”.",
  "store.search.short": "Type at least two characters.",
  "store.genres": "Genres",
  "store.rail.prev": "Previous in {rail}",
  "store.rail.next": "Next in {rail}",
  "store.rail.seeAll": "See all",
  "store.rail.seeAllIn": "See all: {rail}",
  "store.rail.error": "Could not load this row.",

  "info.title": "Information",
  "info.kind.video": "Video",
  "info.kind.videoLength": "Video · {length}",
  "info.kind.pdf": "PDF",
  "info.kind.pdfPage": "PDF · 1 page",
  "info.kind.pdfPages": "PDF · {count} pages",
  "info.emptyTitle": "No information yet",
  "info.empty":
    "Video clips and documents from {portal} appear here when they are added.",
  "info.gone": "This is no longer available",
  "info.backToList": "To all information",
  "info.download": "Download",
  "info.page": "Page {page} of {count}",
  "info.clipFailed":
    "The clip could not be played. Reload the page and try again.",
  "info.pdfFailed":
    "The document could not be shown. You can download it instead.",
  "store.allTitles": "All films",
  "genre.sortBy": "Sort by",
  "genre.sort.popular": "Most popular",
  "genre.sort.az": "A–Z",
  "genre.sort.year": "Release year",
  "genre.sort.length": "Length",
  "genre.notFound": "This genre is not available here.",
  "genre.count": "{count} titles",
  "card.minutes": "{n} min",
  "card.hoursMinutes": "{h} h {m} min",

  "title.locked": "Not rented",
  "title.rented": "Rented",
  "title.notFound": "This title is not available here.",
  "title.addToList": "My list",
  "title.addToListLabel": "Add to My list",
  "title.onList": "On My list",
  "title.removeFromListLabel": "Remove from My list",
  "title.listFailed": "Could not change My list. Try again.",
  "title.rentedEnds": "Rented · ends in {time}",
  "title.rentedStartWithin": "Rented · start within {time}",
  "title.rentedNoLimit": "Rented · no time limit",
  "title.rentedExpired": "The rental has ended",
  "title.originalTitle": "Original title: {title}",
  "title.director": "Director",
  "title.cast": "Cast",
  "title.audio": "Audio",
  "title.play": "Play",
  "title.playEpisode": "Play S{s}:E{e}",
  "title.episodeList": "Episodes",
  "title.seasonTabs": "Season",
  "title.seasonN": "Season {n}",
  "title.episodeLocked": "Not in your rental",
  "title.episodeExpired": "The rental has ended",
  "title.subtitles": "Subtitles",
  "title.aboutFilm": "About the film",
  "title.aboutSeries": "About the series",
  "title.details": "Details",
  "title.seasons": "{n} seasons",
  "title.season": "1 season",
  "title.episodes": "{n} episodes",
  "title.age": "Age rating {age}",
  "title.ageAll": "Suitable for all ages",
  "title.score.imdb": "IMDb rating: {score} out of 10",
  "title.score.tmdb": "TMDB rating: {score} out of 10",
  "title.score.provider": "Rating from the provider: {score} out of 10",
  "title.score.manual": "Rating entered manually: {score} out of 10",
  "title.score.votes": "{n} votes",

  "return.browse": "Back to the films",

  "my.title": "My content",
  "my.loginFirst": "Log in to see your content.",
  "my.empty": "You have not rented anything yet.",
  "my.active": "Watch now",
  "my.notStarted": "Not started",
  "my.expired": "Expired",
  "my.left": "{time} left",
  "my.noLimit": "No time limit",
  "my.startBy": "Start by {date}",
  "my.clockOnPlay":
    "The rental period starts when you start the title in the app.",
  "my.endedAt": "Expired {date}",
  "time.lessThanMinute": "less than 1 min",

  "auth.loginTitle": "Log in",
  "auth.login": "Log in",
  "auth.loggingIn": "Logging in…",
  "auth.invalid": "Wrong email address or password.",
  "auth.notVerified":
    "Confirm your email address first - look for the mail from us.",
  "auth.disabled": "This account is disabled.",
  "auth.rateLimited": "Too many attempts. Wait a while and try again.",
  "auth.forgot": "Forgot your password?",
  "auth.noAccount": "No account?",
  "auth.resend": "Send the confirmation link again",
  "auth.name": "Name (optional)",
  "apply.title": "Apply for an account",
  "apply.intro":
    "Accounts with {provider} are for organisations such as schools and libraries. Fill in the details below and we will review the application. You will get an email when the account is ready to use.",
  "apply.companyName": "Company name",
  "apply.orgNumber": "Organisation number",
  "apply.orgNumberHint": "For example 556074-3089",
  "apply.attention": "Attn",
  "apply.street": "Address",
  "apply.postcode": "Postcode",
  "apply.city": "Town",
  "apply.contactEmail": "Contact person's email",
  "apply.invalid.companyName": "Enter the organisation's name.",
  "apply.invalid.orgNumber":
    "Check the organisation number - it should be 10 digits, for example 556074-3089.",
  "apply.invalid.attention": "Enter who we should contact.",
  "apply.invalid.street": "Enter the street address.",
  "apply.invalid.postcode": "The postcode should be five digits.",
  "apply.invalid.city": "Enter the town.",
  "apply.invalid.contactEmail": "Enter a valid email address.",
  "apply.band": "How many watch one screening?",
  "apply.bandHint":
    "The number of people watching the film together, for example a class.",
  "apply.level": "How many films?",
  "apply.film1": "choose 1 film",
  "apply.films": "choose {n} films",
  "apply.fullLibrary": "every film",
  "apply.perYear": "{price} per year ex VAT ({inclVat} incl. VAT)",
  "apply.invalid.plan": "Choose how many watch and how many films.",
  "apply.closed": "{provider} is not taking applications right now.",
  "apply.send": "Send application",
  "apply.sending": "Sending…",
  "apply.doneTitle": "Thank you for applying",
  "apply.done":
    "{provider} will review the application and get back to {email}. Once it is approved you will get an email with a link to choose a password.",
  "apply.backToStore": "Back to the films",
  "auth.haveAccount": "Already have an account?",
  "auth.tooShort": "The password must be at least 10 characters.",
  "auth.tooLong": "The password is too long.",
  "auth.breached":
    "This password has appeared in a data breach. Choose another one.",
  "auth.badRequest": "Check your details and try again.",
  "auth.confirmTitle": "Confirm email address",
  "auth.confirming": "Confirming…",
  "auth.confirmed": "Your email address is confirmed. You can now log in.",
  "auth.badLink": "The link is invalid or has expired.",
  "auth.noToken":
    "The link is missing its code. Open the link straight from the mail.",
  "auth.resendTitle": "Send the confirmation link again",
  "auth.resendSend": "Send",
  "auth.resendSent":
    "If there is an unconfirmed account for {email}, we have sent a new link there.",
  "auth.forgotTitle": "Forgot password",
  "auth.forgotSend": "Send reset link",
  "auth.forgotSent":
    "If there is an account for {email}, we have sent a link there. It is valid for one hour.",
  "auth.resetTitle": "Choose a new password",
  "auth.newPassword": "New password",
  "auth.resetSave": "Save password",
  "auth.resetDone":
    "Your password is changed and every device has been logged out. Log in with the new password.",
  "auth.inviteTitle": "Welcome",
  "auth.inviteBody":
    "You have been invited through your school. Choose a password to activate the account.",
  "auth.inviteSave": "Activate account",
  "auth.inviteDone": "The account for {email} is active. You can now log in.",
  "auth.inviteDoneSwitched":
    "The account for {email} is active. You were signed in as {previous} and have been signed out - log in as {email}.",
  "auth.signedInAs": "You are signed in as {email}.",
  "auth.continueAs": "Continue",
  "auth.switchAccount": "Log out and switch account",
  "auth.inviteGone": "The invitation is invalid, already used or has expired.",
  "auth.signedOut": "You are logged out.",

  "account.title": "Account",
  "account.loginFirst": "Log in to see your account.",
  "account.profile": "Profile",
  "account.name": "Name",
  "account.locale": "Language for emails",
  "account.marketing": "News and offers by email",
  "account.currentPassword": "Current password",
  "account.wrongPassword": "Wrong password.",
  "account.passwordTitle": "Change password",
  "account.passwordChange": "Change password",
  "account.passwordChanged":
    "Your password is changed. Your other devices have been logged out.",
  "account.devices": "Signed-in devices",
  "account.device.web": "Browser",
  "account.device.app": "App",
  "account.device.current": "This device",
  "account.device.lastUsed": "Last used {date}",
  "account.device.signOut": "Log out",
  "account.device.none": "No signed-in devices.",
  "account.logout": "Log out",
  "account.logoutAll": "Log out of every device",
  "account.logoutAllBody":
    "Logs out this browser and every app where you are signed in.",
  "account.unverified": "Your email address is not confirmed yet.",

  /* Store design (portal appearance) */
  "card.expiresIn": "Expires in {time}",
  "card.notStarted": "Rented · not started",
  "card.noLimit": "Rented · no time limit",
  "card.expired": "The rental has ended",
  "store.genres.prev": "Previous genres",
  "store.genres.next": "More genres",
  "store.empty.title": "The store is being stocked",
  "store.empty.body":
    "There is nothing to rent here right now. Look in again a little later.",
  "store.search.emptyHint": "Try another word, or browse a genre.",
  "my.status.active": "Watch now",
  "my.status.notStarted": "Not started",
  "my.status.expired": "Expired",
  "my.emptyTitle": "What you rent lands here",
  "my.discover": "Discover the films",
  "my.list": "My list",
  "my.listEmpty":
    "Nothing saved yet. Press My list on a film's page to save it here.",
  "footer.nav": "About the service",
  "footer.tmdb": "Some information and ratings come from",
  "footer.tmdbNotice":
    "This product uses the TMDB API but is not endorsed or certified by TMDB.",
};

const DICTS: Record<Lang, Record<MessageKey, string>> = { sv, en };

export function format(
  template: string,
  vars: Record<string, string | number> = {}
): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars ? String(vars[k]) : m
  );
}

export type Translate = (
  key: MessageKey,
  vars?: Record<string, string | number>
) => string;

export function translator(lang: Lang): Translate {
  const dict = DICTS[lang] ?? sv;
  return (key, vars) => format(dict[key] ?? sv[key] ?? key, vars);
}

export interface I18n {
  lang: Lang;
  t: Translate;
}

export const I18nContext = createContext<I18n>({
  lang: "sv",
  t: translator("sv"),
});

export function useI18n(): I18n {
  return useContext(I18nContext);
}

export function isLang(v: unknown): v is Lang {
  return v === "sv" || v === "en";
}
